import { useCallback, useMemo } from 'react'
import keyBy from 'lodash/keyBy'
import { TIconName } from '../components/action-list'
import { useUserContext } from '../context'
import { SIGNUP_INTENT } from '../types'
import { ISetupGuideStep, useGetSetupGuide, IUseGetSetupGuideProps } from './useGetSetupGuide'

interface IUseSetupGuideProps {
  useGetSetupGuideProps?: IUseGetSetupGuideProps
}

/**
 * Used to manage state and data for setup guide
 */
export const useSetupGuide = ({ useGetSetupGuideProps = {} }: IUseSetupGuideProps = {}) => {
  const { userData } = useUserContext()
  const { settings } = userData || {}
  const { signupIntents = [] } = settings || {}

  // API calls and state
  const { data, loading } = useGetSetupGuide(useGetSetupGuideProps)
  const { setupGuide } = data ?? {}
  const { steps = [], dismissed: isSetupGuideDismissed } = setupGuide || {}

  // Forms array into easily accessible object
  const stepsByKey = useMemo(() => keyBy(steps, step => step.type), [steps])

  const getStepByKeyName = useCallback(
    (keyName: ISetupGuideStep['type']): ISetupGuideStep => {
      return stepsByKey[keyName] || {}
    },
    [stepsByKey]
  )

  const getStepStatus = useCallback(
    (keyName: ISetupGuideStep['type']): ISetupGuideStep['status'] => {
      const { status } = getStepByKeyName(keyName)
      return status
    },
    [getStepByKeyName]
  )

  const isStepStatus = useCallback(
    (keyName: ISetupGuideStep['type'], status: ISetupGuideStep['status']): boolean => {
      return status === getStepStatus(keyName)
    },
    [getStepStatus]
  )

  const getStepIcon = useCallback(
    (keyName: ISetupGuideStep['type']): TIconName => {
      switch (getStepStatus(keyName)) {
        case 'incomplete':
          return '25'
        case 'complete':
          return '100'
        default:
          return '0'
      }
    },
    [getStepStatus]
  )

  const isSetupGuideComplete = useCallback(
    ({ isKeepTruckinEnabled = false } = {}) => {
      const choseFleetPlannerOnSignUp = signupIntents.includes(SIGNUP_INTENT.manageFleet)
      if (choseFleetPlannerOnSignUp) {
        const setupTasks = [
          isStepStatus('create_load', 'complete'),
          isStepStatus('invite_team_members', 'complete'),
          isStepStatus('carrier_onboarding', 'complete'),
        ]

        if (isKeepTruckinEnabled) {
          setupTasks.push(isStepStatus('connect_eld', 'complete'))
        }

        return setupTasks.every(item => !!item)
      }

      return isStepStatus('carrier_onboarding', 'complete')
    },
    [isStepStatus, signupIntents]
  )

  return {
    loading,
    data,
    isStepStatus,
    getStepIcon,
    getStepByKeyName,
    steps,
    isSetupGuideComplete,
    isSetupGuideDismissed,
  }
}
